<mat-toolbar color="primary" *ngIf="isDialog">
  <mat-toolbar-row>
    <span>{{ question?.question }}</span>
    <span class="modal-toolbar-spacer"></span>
    <button mat-icon-button class="ml-auto" (click)="closeDialogue()">
      <mat-icon>close</mat-icon>
    </button>
  </mat-toolbar-row>
</mat-toolbar>

<!-- Loading Indicator -->
<div class="row" *ngIf="loading; else contentBlock">
  <mat-progress-bar mode="indeterminate" color="primary"></mat-progress-bar>
</div>

<!-- Main Content -->
<ng-template #contentBlock>
  <div [ngClass]="isDialog ? 'container mt-5' : ''">

    <!-- Word Cloud for Opened Questions -->
    <div *ngIf="surveyService.isOpenedQuestion(question?.typology) && question.content && questionContentLoaded"
         class="row justify-content-center mt-5">
      <img class="w-100"
           [src]="'https://quickchart.io/wordcloud?height=300&width=600&language=it&removeStopwords=true&fontScale=150&text=' + question.content">
    </div>

    <ng-container *ngIf="surveyService.isGridQuestion(question.typology)">
      <mat-tab-group mat-stretch-tabs mat-align-tabs="center" [(selectedIndex)]="selectedTabIndex">
        <mat-tab label="Heatmap" *ngIf="heatmapOptions">
          <div class="container my-5 justify-content-center row">
            <div style="width: 100%; height: 800px" echarts [options]="heatmapOptions"></div>
          </div>
        </mat-tab>
        <mat-tab [label]="translateService.instant('generic.pie-charts')">
          <div class="container justify-content-center row">
            <div *ngFor="let row of question.grid_rows" class="col-6 mb-4">
              <div class="card h-100">
                <div class="card-header">{{ row.title }}</div>
                <div class="card-body p-2">
                  <div style="width: 100%; height: 200px;" echarts [options]="gridChartOptionsMap[row.id]"></div>
                </div>
              </div>
            </div>
          </div>
        </mat-tab>
      </mat-tab-group>
    </ng-container>
    <ng-container *ngIf="surveyService.isMultipleChoiceQuestion(question?.typology)">
      <div class="container my-5 justify-content-center row">
        <div style="width: 100%; height: 400px;" echarts [options]="multipleChoiceChartOptions"></div>
      </div>
    </ng-container>
    <ng-container *ngIf="surveyService.isCheckboxQuestion(question?.typology)">
      <div class="container my-5 justify-content-center row">
        <div style="width: 70%; height: 400px;" echarts [options]="checkboxChartOptions"></div>
      </div>
    </ng-container>
    <ng-container *ngIf="surveyService.isConsensusQuestion(question?.typology)">
      <div class="row">
        <div class="col-6">
          <div style="width: 100%; height: 300px;" echarts [options]="consensusParametersChartOptions"></div>
        </div>
        <div class="col-6">
          <div style="width: 100%; height: 300px;" echarts [options]="consensusChartOptions"></div>
        </div>
      </div>
    </ng-container>
    <ng-container *ngIf="surveyService.isQuantitativeQuestion(question?.typology)">
      <div class="container my-5 justify-content-center row">
        <div style="width: 100%; height: 400px;" echarts [options]="quantitativeChartOptions"></div>
      </div>
      <div class="container justify-content-center row">
        <div class="col-6">
          <div style="width: 100%; height: 300px;" echarts [options]="boxPlotChartOptions"></div>
        </div>
        <div class="col-6">
          <mat-table [dataSource]="analyticsFields" class="my-2">
            <ng-container matColumnDef="parameter">
              <mat-header-cell *matHeaderCellDef class="font-weight-bold">{{ "generic.parameter" | translate }}
              </mat-header-cell>
              <mat-cell *matCellDef="let element; let i = index">{{ element.translateString | translate }}</mat-cell>
            </ng-container>
            <ng-container matColumnDef="value">
              <mat-header-cell *matHeaderCellDef class="font-weight-bold">{{ "generic.value" | translate }}
              </mat-header-cell>
              <mat-cell *matCellDef="let element; let i = index">
                <ng-container *ngIf="analyticsFields[i].parameter === 'mode'; else defaultValue">
                  <ng-container *ngIf="question?.quantitative_results?.mode?.length > 0; else noValue">
                    {{ question?.quantitative_results?.mode?.join(', ') }}
                  </ng-container>
                  <ng-template #noValue>N/A</ng-template>
                </ng-container>
                <ng-template #defaultValue>
                  {{ question?.quantitative_results?.[analyticsFields[i].parameter] ?? 'N/A' }}
                </ng-template>
              </mat-cell>
            </ng-container>
            <mat-header-row *matHeaderRowDef="['parameter', 'value']"></mat-header-row>
            <mat-row *matRowDef="let row; columns: ['parameter', 'value']"></mat-row>
          </mat-table>
        </div>
      </div>
    </ng-container>

    <!-- AI Analysis -->
    <ng-container *ngIf="!viewOnlyChart">
      <div [ngClass]="isDialog ? 'mt-5' : ''">

        <div *appShowIfOrganizationHasFeature="'virtual_assistant'">
          <app-single-question-ai-analysis type="survey" [question]="question"/>
        </div>

        <!-- Responses Table -->
        <mat-table class="table-primary" [dataSource]="responses">
          <!-- Name Column -->
          <ng-container matColumnDef="name">
            <mat-header-cell *matHeaderCellDef>{{ "generic.name" | translate }}</mat-header-cell>
            <mat-cell *matCellDef="let element; let i = index">
              {{ showPrivateInfo ? (element.name || 'N/A') : ('generic.user' | translate) + ' ' + (i + 1) }}
            </mat-cell>
          </ng-container>

          <!-- Sentiment Column -->
          <ng-container matColumnDef="sentiment">
            <mat-header-cell *matHeaderCellDef>{{ "generic.sentiment" | translate }}</mat-header-cell>
            <mat-cell *matCellDef="let element">
              <mat-icon *ngIf="!element?.score">sentiment_neutral</mat-icon>
              <mat-icon style="color: #00bc1d" *ngIf="element?.score > 0">sentiment_satisfied_alt</mat-icon>
              <mat-icon style="color: #ff0000" *ngIf="element?.score < 0">sentiment_very_dissatisfied</mat-icon>
            </mat-cell>
          </ng-container>

          <!-- Answer Column -->
          <ng-container matColumnDef="answer">
            <mat-header-cell *matHeaderCellDef>{{ "generic.response" | translate }}</mat-header-cell>
            <mat-cell *matCellDef="let element">
              <ng-container *ngIf="surveyService.isCheckboxQuestion(question?.typology)">
                <ul class="m-0 p-0" *ngIf="!element?.answer?.includes('N/A'); else noAnswer">
                  <li *ngFor="let choice of element?.answer">{{ choice }}</li>
                </ul>
                <ng-template #noAnswer>N/A</ng-template>
              </ng-container>
              <ng-container *ngIf="surveyService.isMultipleChoiceQuestion(question?.typology)">
                {{ element?.answer }}<br>{{ element?.open_response_value }}
              </ng-container>
              <ng-container *ngIf="surveyService.isConsensusQuestion(question?.typology)">
                <mat-chip-list>
                  <mat-chip *ngIf="responsesTypeService.isConsensusAgree(element.answer)"
                            selectable="false"
                            style="justify-content: center; pointer-events: none; background-color: #00bc1d;"
                            class="text-white">
                    {{ "generic.agree" | translate }}
                  </mat-chip>
                  <mat-chip *ngIf="responsesTypeService.isConsensusDisagree(element.answer)"
                            selectable="false"
                            style="justify-content: center; pointer-events: none; background-color: #ff0000;"
                            class="text-white">
                    {{ "generic.disagree" | translate }}
                  </mat-chip>
                  <mat-chip *ngIf="responsesTypeService.isConsensusAbstained(element.answer)"
                            selectable="false"
                            style="justify-content: center; pointer-events: none; background-color: rgb(167, 167, 167);"
                            class="text-white">
                    {{ "generic.abstained" | translate }}
                  </mat-chip>
                </mat-chip-list>
              </ng-container>
              <ng-container
                *ngIf="surveyService.isQuantitativeQuestion(question?.typology) || surveyService.isOpenedQuestion(question?.typology)">
                {{ element.answer }}
              </ng-container>
            </mat-cell>
          </ng-container>

          <!-- Feedback Column -->
          <ng-container matColumnDef="feedback">
            <mat-header-cell *matHeaderCellDef>{{ "generic.feedback" | translate }}</mat-header-cell>
            <mat-cell *matCellDef="let element">
              {{ element.feedback || 'N/A' }}
            </mat-cell>
          </ng-container>

          <!-- Confidence Column -->
          <ng-container matColumnDef="confidence">
            <mat-header-cell class="small-column" *matHeaderCellDef>
              {{ "components.proposals.new-proposal.confidence" | translate }}
            </mat-header-cell>
            <mat-cell class="small-column" *matCellDef="let element">
              <mat-chip-list>
                <mat-chip>{{ element?.confidence || 'N/A' }}</mat-chip>
              </mat-chip-list>
            </mat-cell>
          </ng-container>

          <!-- Attachments Column -->
          <ng-container matColumnDef="evidences">
            <mat-header-cell *matHeaderCellDef>{{ "generic.attachments" | translate }}</mat-header-cell>
            <mat-cell *matCellDef="let element">
              <mat-chip-list>
                <mat-chip (click)="element?.attachments?.length > 0 ? showExpertEvideces(element) : null">
                  {{ element?.attachments?.length }}
                </mat-chip>
              </mat-chip-list>
            </mat-cell>
          </ng-container>

          <!-- Table Rows -->
          <mat-header-row *matHeaderRowDef="columnsForTypology"></mat-header-row>
          <mat-row *matRowDef="let element; columns: columnsForTypology;"></mat-row>
        </mat-table>
      </div>
    </ng-container>
  </div>
</ng-template>
